import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import FotoTabPDF from './FotoTabPDF';
import FeatureGate from '../shared/FeatureGate';  // CAP-008
import UpsellBlock from '../shared/UpsellBlock';  // CAP-008
import { useAuth } from '../../context/AuthContext';

const slugify = s => s.toLowerCase().replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e').replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o').replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

const LABELS = [
  { value: 'prima_dei_lavori', label: 'Prima dei lavori' },
  { value: 'in_corso', label: 'In corso' },
  { value: 'fine_lavori', label: 'Fine lavori' },
  { value: 'problema', label: 'Problema / Difetto' },
  { value: 'materiale', label: 'Materiale' },
  { value: 'altro', label: 'Altro' },
];

const getLabelText = (value) => LABELS.find(l => l.value === value)?.label || value || 'Altro';

// Video WhatsApp: EventoCantiere tipo='foto' + metadata.media_kind='video'
const isVideoEvento = (f) => f?.metadata?.media_kind === 'video';

const fmtGroupDate = (d) => {
  try { return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }); }
  catch { return d; }
};

export default function FotoTab({ cantiereId, dashData }) {
  const { hasFeature } = useAuth();  // CAP-008 — foto incluse solo nei piani a pagamento
  const canUpload = hasFeature('upload_foto_cantiere');
  const [foto, setFoto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filtroAttivo, setFiltroAttivo] = useState(null);
  const [labelUpload, setLabelUpload] = useState('in_corso');
  const [menuFotoId, setMenuFotoId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [selezionate, setSelezionate] = useState(new Set());

  const fetchFoto = useCallback(() => {
    setLoading(true);
    cantieri.eventi(cantiereId)
      .then(res => {
        const data = res.data?.results || res.data || [];
        setFoto(data.filter(e => e.tipo === 'foto' && (e.allegato || e.allegato_url)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cantiereId]);

  useEffect(() => { fetchFoto(); }, [fetchFoto]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const allowed = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowed.includes(file.type)) continue;
        await cantieri.addEvento(cantiereId, {
          tipo: 'foto',
          contenuto: file.name,
          metadata: { label: labelUpload },
        }, file);
      }
      fetchFoto();
    } catch {}
    finally { setUploading(false); }
  };

  const handleChangeLabel = (fotoId, newLabel) => {
    setFoto(prev => prev.map(f =>
      f.id === fotoId ? { ...f, metadata: { ...(f.metadata || {}), label: newLabel } } : f
    ));
    setMenuFotoId(null);
    cantieri.patchEvento(cantiereId, fotoId, { metadata: { label: newLabel } }).catch(() => fetchFoto());
  };

  const handleDeleteSelezionate = async () => {
    if (selezionate.size === 0) return;
    if (!window.confirm('Eliminare ' + selezionate.size + ' foto selezionate?')) return;
    for (const fotoId of selezionate) {
      try { await cantieri.deleteEvento(cantiereId, fotoId); } catch {}
    }
    setSelezionate(new Set());
    fetchFoto();
  };

  const toggleSelezione = (fotoId) => {
    setSelezionate(prev => {
      const next = new Set(prev);
      if (next.has(fotoId)) next.delete(fotoId);
      else next.add(fotoId);
      return next;
    });
  };

  // Chiudi menu al click fuori
  useEffect(() => {
    if (!menuFotoId) return;
    const handler = () => setMenuFotoId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuFotoId]);

  if (loading) return <div style={{ padding: 20, color: 'var(--ds-muted)' }}>Caricamento foto...</div>;

  const fotoFiltrate = filtroAttivo
    ? foto.filter(f => (f.metadata?.label || 'altro') === filtroAttivo)
    : foto;

  const grouped = {};
  fotoFiltrate.forEach(f => {
    const label = f.metadata?.label || 'altro';
    const dateKey = f.data_riferimento || f.created_at?.slice(0, 10) || '';
    const groupKey = getLabelText(label).toUpperCase() + (dateKey ? ' · ' + fmtGroupDate(dateKey) : '');
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(f);
  });
  const sortedKeys = Object.keys(grouped);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Foto & Video</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selezionate.size > 0 && (
            <button onClick={handleDeleteSelezionate} style={{
              padding: '10px 20px', borderRadius: 6, border: '1px solid #E5533D40',
              background: 'var(--ds-bg)', color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}>
              Elimina ({selezionate.size})
            </button>
          )}
          {foto.length > 0 && (
            <PDFDownloadLink
              document={<FotoTabPDF foto={foto.filter(f => !isVideoEvento(f))} cantiere={dashData?.cantiere} azienda={dashData?.azienda} />}
              fileName={'foto-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
            >
              {({ loading }) => (
                <button style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12 }} disabled={loading}>
                  {loading ? '...' : '📄 PDF'}
                </button>
              )}
            </PDFDownloadLink>
          )}
          <FeatureGate flag="upload_foto_cantiere" hideWhenOff>
            <label style={{
              padding: '10px 20px', borderRadius: 6, border: 'none',
              background: 'var(--brand)', color: '#fff', cursor: uploading ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 500, opacity: uploading ? 0.5 : 1,
            }}>
              📸 {uploading ? 'Caricamento...' : 'Aggiungi'}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} disabled={uploading} />
            </label>
          </FeatureGate>
        </div>
      </div>

      {!canUpload && (
        <UpsellBlock
          titolo="Foto di cantiere"
          messaggio="Le foto di cantiere sono incluse nei piani a pagamento — passa a Smart, Business o Pro per archiviarle e riceverle via WhatsApp."
        />
      )}

      {/* Chip FILTRI */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Filtra:</span>
        <FilterChip label={'Tutte (' + foto.length + ')'} active={filtroAttivo === null} onClick={() => setFiltroAttivo(null)} />
        {LABELS.map(l => {
          const count = foto.filter(f => (f.metadata?.label || 'altro') === l.value).length;
          if (count === 0) return null;
          return <FilterChip key={l.value} label={l.label + ' (' + count + ')'} active={filtroAttivo === l.value} onClick={() => setFiltroAttivo(filtroAttivo === l.value ? null : l.value)} />;
        })}
      </div>

      {/* Select label per nuovi upload — UI morta se il piano non include le foto */}
      {canUpload && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Nuove foto come:</span>
          <select value={labelUpload} onChange={e => setLabelUpload(e.target.value)} style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid var(--ds-border)',
            fontSize: 12, background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer',
          }}>
            {LABELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
      )}

      {fotoFiltrate.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📸</div>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ds-text)' }}>
            {filtroAttivo ? 'Nessuna foto in questa categoria' : 'Nessuna foto'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 4 }}>
            {filtroAttivo
              ? 'Prova a selezionare "Tutte".'
              : (canUpload ? 'Carica foto o inviale via WhatsApp.' : 'Disponibile con un piano a pagamento.')}
          </div>
        </div>
      ) : (
        sortedKeys.map(groupKey => (
          <div key={groupKey} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
              {groupKey}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 'var(--radius-base)' }}>
              {grouped[groupKey].map(f => (
                <FotoCard
                  key={f.id}
                  foto={f}
                  selected={selezionate.has(f.id)}
                  onToggleSelect={() => toggleSelezione(f.id)}
                  onView={() => setLightbox({ url: f.allegato_url || f.allegato, video: isVideoEvento(f) })}
                  menuOpen={menuFotoId === f.id}
                  onToggleMenu={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setMenuPos({ top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 180) });
                    setMenuFotoId(menuFotoId === f.id ? null : f.id);
                  }}
                  onChangeLabel={(label) => handleChangeLabel(f.id, label)}
                />
              ))}
              <FeatureGate flag="upload_foto_cantiere" hideWhenOff>
                <label style={{
                  aspectRatio: '1/1', borderRadius: 'var(--radius-card)',
                  border: '2px dashed var(--ds-border)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 12, gap: 4,
                }}>
                  <span style={{ fontSize: 24 }}>+</span>
                  Aggiungi
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                    onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
                </label>
              </FeatureGate>
            </div>
          </div>
        ))
      )}

      {/* Menu ⋮ posizionato fixed — non viene tagliato */}
      {menuFotoId && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 3000,
          background: 'var(--chip)', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 170, padding: '4px 0',
        }}>
          <div style={{ padding: '6px 12px', fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>
            Cambia stato foto
          </div>
          {LABELS.map(l => {
            const isCurrent = (foto.find(f => f.id === menuFotoId)?.metadata?.label || 'altro') === l.value;
            return (
              <button key={l.value} onClick={() => handleChangeLabel(menuFotoId, l.value)} style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', border: 'none', cursor: 'pointer',
                fontSize: 12, background: isCurrent ? 'rgba(46,160,132,0.12)' : 'transparent',
                color: isCurrent ? 'var(--brand)' : 'var(--ds-text)',
                fontWeight: isCurrent ? 500 : 400,
              }}>
                {isCurrent ? '✓ ' : ''}{l.label}
              </button>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          {lightbox.video ? (
            <video
              src={lightbox.url}
              controls
              autoPlay
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 'var(--radius-base)' }}
            />
          ) : (
            <img src={lightbox.url} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 'var(--radius-base)' }} />
          )}
        </div>
      )}
    </>
  );
}

// ── Componenti helper ──────────────────────────────────────────────────────

function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: active ? '2px solid var(--brand)' : '1px solid var(--ds-border)',
      background: active ? 'rgba(46,160,132,0.12)' : 'var(--ds-bg)',
      color: active ? 'var(--brand)' : 'var(--ds-muted)',
    }}>
      {label}
    </button>
  );
}

function FotoCard({ foto, selected, onToggleSelect, onView, menuOpen, onToggleMenu }) {
  const [hovered, setHovered] = useState(false);
  const currentLabel = foto.metadata?.label || 'altro';
  const isVideo = isVideoEvento(foto);
  const src = foto.allegato_url || foto.allegato;

  return (
    <div
      style={{
        aspectRatio: '1/1', borderRadius: 'var(--radius-card)', overflow: 'hidden',
        position: 'relative', cursor: 'pointer',
        border: selected ? '2px solid var(--brand)' : '1px solid var(--ds-border)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ width: '100%', height: '100%' }} onClick={onView}>
        {isVideo ? (
          <>
            <video src={src} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 16, pointerEvents: 'none',
            }}>▶</div>
          </>
        ) : (
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </div>

      {/* Badge label */}
      <div style={{
        position: 'absolute', bottom: 4, left: 4,
        fontSize: 9, padding: '1px 6px', borderRadius: 4,
        background: 'rgba(0,0,0,0.6)', color: '#fff',
      }}>
        {getLabelText(currentLabel)}
      </div>

      {/* Checkbox selezione — visibile su hover o se selezionata */}
      {(hovered || selected) && (
        <button onClick={(e) => { e.stopPropagation(); onToggleSelect(); }} style={{
          position: 'absolute', top: 4, left: 4,
          width: 24, height: 24, borderRadius: 4,
          border: selected ? '2px solid var(--brand)' : '2px solid rgba(255,255,255,0.8)',
          background: selected ? 'var(--brand)' : 'rgba(0,0,0,0.3)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14,
        }}>
          {selected ? '✓' : ''}
        </button>
      )}

      {/* Bottone ⋮ */}
      {hovered && (
        <button onClick={onToggleMenu} style={{
          position: 'absolute', top: 4, right: 4,
          width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(0,0,0,0.5)', border: 'none',
          color: '#fff', cursor: 'pointer', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          ⋮
        </button>
      )}
    </div>
  );
}
